import React, { useState, useRef, useEffect } from 'react';
import './NightPhase.css';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { useGame } from '../../contexts/GameContext';
import { recordCardAction, processNextTurn } from '../../firebase/services';
import { getCardById } from '../../data/cards';

const NightPhase = () => {
  const { 
    lobby, 
    playerId, 
    currentPlayer, 
    currentCard, 
    isMyTurn 
  } = useGame();
  
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedCenterCardIndex, setSelectedCenterCardIndex] = useState(null);
  const [secondSelectedPlayerId, setSecondSelectedPlayerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [isShowingActionResult, setIsShowingActionResult] = useState(false);
  
  const hasProcessedActionRef = useRef(false);

  useEffect(() => {
    if (actionResult) {
      setIsShowingActionResult(true);
      hasProcessedActionRef.current = true;
    }
  }, [actionResult]);

  if (!lobby || !currentPlayer) {
    return <div className="night-loading">Yükleniyor...</div>;
  }

  const cardData = currentCard ? getCardById(currentCard) : null;
  const hasCompletedTurn = lobby.turnManager?.completedTurns?.some(
    action => action.playerId === playerId
  ) ?? false;
  
  // Handle closing the action result and proceeding to the next turn
  const handleCloseActionResult = async () => {
    try {
      // Now that the player has acknowledged the result, process the next turn
      await processNextTurn(lobby.code);
      
      // Reset state after processing
      setActionResult(null);
      setIsShowingActionResult(false);
      hasProcessedActionRef.current = false;
    } catch (error) {
      console.error('Error processing next turn:', error);
    }
  };
  
  // Handle card action submission
  const handleSubmitAction = async () => {
    if (!isMyTurn || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      let actionData = {
        playerId,
        action: cardData.action,
        timestamp: new Date()
      };
      
      // Add action-specific data
      switch (cardData.action) {
        case 'VIEW_PLAYER_CARD':
        case 'COPY_PLAYER_CARD':
        case 'SILENCE_PLAYER':
        case 'REVEAL_PLAYER_CARD':
        case 'REVEAL_AT_END': // Zeus's ability
        case 'STEAL_CARD': // Hermes's ability
          if (!selectedPlayerId) {
            throw new Error('Lütfen bir oyuncu seçin');
          }
          actionData.targetPlayerId = selectedPlayerId;
          // For COPY_PLAYER_CARD, get target player's card
          if (cardData.action === 'COPY_PLAYER_CARD') {
            const targetPlayer = lobby.players.find(p => p.id === selectedPlayerId);
            actionData.targetCard = targetPlayer.card;
          }
          // For STEAL_CARD, store target player's card
          if (cardData.action === 'STEAL_CARD') {
            const targetPlayer = lobby.players.find(p => p.id === selectedPlayerId);
            actionData.targetCard = targetPlayer.card;
            actionData.originalCard = currentCard;
          }
          break;
          
        case 'VIEW_CENTER_CARD':
          if (selectedCenterCardIndex === null) {
            throw new Error('Lütfen ortadaki bir kart seçin');
          }
          actionData.centerCardIndex = selectedCenterCardIndex;
          break;
          
        case 'SWAP_PLAYER_CARDS':
          if (!selectedPlayerId || !secondSelectedPlayerId) {
            throw new Error('Lütfen iki oyuncu seçin');
          }
          actionData.targetPlayerIds = [selectedPlayerId, secondSelectedPlayerId];
          break;
          
        case 'SWAP_WITH_CENTER':
          if (selectedCenterCardIndex === null) {
            throw new Error('Lütfen ortadaki bir kart seçin');
          }
          actionData.centerCardIndex = selectedCenterCardIndex;
          break;
          
        case 'RANDOM_CENTER_CARD': // Dionysos's ability
          // Pick a random center card
          const randomIndex = Math.floor(Math.random() * lobby.centerCards.length);
          actionData.centerCardIndex = randomIndex;
          actionData.randomCard = lobby.centerCards[randomIndex];
          break;

        case 'VIEW_TWO_CARDS_CHANGE_ROLE': // Gareth's ability
          if (!selectedPlayerId || !secondSelectedPlayerId) {
            throw new Error('Lütfen iki oyuncu seçin');
          }
          actionData.targetPlayerIds = [selectedPlayerId, secondSelectedPlayerId];
          // Get target players' cards
          const firstPlayer = lobby.players.find(p => p.id === selectedPlayerId);
          const secondPlayer = lobby.players.find(p => p.id === secondSelectedPlayerId);
          actionData.targetCards = [firstPlayer.card, secondPlayer.card];
          
          // Check if one of them is werewolf or tanner, if so, change role
          const firstPlayerCard = getCardById(firstPlayer.card);
          const secondPlayerCard = getCardById(secondPlayer.card);
          
          if (firstPlayerCard.team === 'werewolf' || firstPlayerCard.team === 'tanner') {
            actionData.changeToCard = firstPlayer.card;
          } else if (secondPlayerCard.team === 'werewolf' || secondPlayerCard.team === 'tanner') {
            actionData.changeToCard = secondPlayer.card;
          }
          break;

        case 'VOTE_KILLS_TARGET': // Thor's ability
          // This is handled during the voting phase, so no action needed here
          actionData.thorAbilityActive = true;
          break;
          
        case 'NONE':
          // No action needed
          break;
          
        default:
          throw new Error('Tanımsız kart aksiyonu');
      }
      
      // Record the action in Firestore, but don't process next turn automatically
      // We'll wait for explicit confirmation from the user
      await recordCardAction(lobby.code, actionData, false);
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // If we need to show a result to the player based on their action
      if (['VIEW_PLAYER_CARD', 'VIEW_CENTER_CARD', 'COPY_PLAYER_CARD', 'VIEW_TWO_CARDS_CHANGE_ROLE', 'RANDOM_CENTER_CARD', 'STEAL_CARD'].includes(cardData.action)) {
        if (cardData.action === 'VIEW_PLAYER_CARD' || cardData.action === 'COPY_PLAYER_CARD') {
          const targetPlayer = lobby.players.find(p => p.id === selectedPlayerId);
          setActionResult({
            type: 'player_card',
            playerName: targetPlayer.name,
            cardId: targetPlayer.card
          });
        } else if (cardData.action === 'VIEW_CENTER_CARD') {
          setActionResult({
            type: 'center_card',
            position: selectedCenterCardIndex + 1,
            cardId: lobby.centerCards[selectedCenterCardIndex]
          });
        } else if (cardData.action === 'VIEW_TWO_CARDS_CHANGE_ROLE') {
          // Show both cards and indicate if role changed
          const firstPlayer = lobby.players.find(p => p.id === selectedPlayerId);
          const secondPlayer = lobby.players.find(p => p.id === secondSelectedPlayerId);
          const firstPlayerCard = getCardById(firstPlayer.card);
          const secondPlayerCard = getCardById(secondPlayer.card);
          
          let changeMessage = '';
          if (firstPlayerCard.team === 'werewolf' || firstPlayerCard.team === 'tanner') {
            changeMessage = `${firstPlayer.name}'in kartı (${firstPlayerCard.name}) kurt ya da düzenbaz! Bu role geçiyorsun.`;
          } else if (secondPlayerCard.team === 'werewolf' || secondPlayerCard.team === 'tanner') {
            changeMessage = `${secondPlayer.name}'in kartı (${secondPlayerCard.name}) kurt ya da düzenbaz! Bu role geçiyorsun.`;
          } else {
            changeMessage = 'Görüntülediğin kartlar arasında kurt veya düzenbaz yok. Rolün değişmedi.';
          }
          
          setActionResult({
            type: 'two_player_cards',
            firstPlayerName: firstPlayer.name,
            firstCardId: firstPlayer.card,
            secondPlayerName: secondPlayer.name,
            secondCardId: secondPlayer.card,
            changeMessage: changeMessage
          });
        } else if (cardData.action === 'RANDOM_CENTER_CARD') {
          setActionResult({
            type: 'random_center_card',
            position: actionData.centerCardIndex + 1,
            cardId: actionData.randomCard,
            message: 'Rastgele seçilen merkez kartına dönüştün!'
          });
        } else if (cardData.action === 'STEAL_CARD') {
          const targetPlayer = lobby.players.find(p => p.id === selectedPlayerId);
          setActionResult({
            type: 'steal_card',
            playerName: targetPlayer.name,
            cardId: targetPlayer.card,
            message: `${targetPlayer.name}'in kartını çaldın!`
          });
        }
      } else if (cardData.action === 'VOTE_KILLS_TARGET') {
        // Thor's ability is passive and will be applied during voting
        setActionResult({
          type: 'success',
          message: 'Thor yeteneğin aktif! Oy verdiğin oyuncu anında ölecek.'
        });
      } else if (cardData.action === 'REVEAL_AT_END') {
        const targetPlayer = lobby.players.find(p => p.id === selectedPlayerId);
        setActionResult({
          type: 'success',
          message: `Oyun sonunda ${targetPlayer.name}'in kartı herkese gösterilecek!`
        });
      } else {
        // No result to show, action completed successfully
        setActionResult({
          type: 'success',
          message: 'Aksiyonun başarıyla tamamlandı!'
        });
      }
    } catch (error) {
      console.error('Card action error:', error);
      setActionResult({
        type: 'error',
        message: error.message || 'Aksiyon gerçekleştirilemedi'
      });
      setIsSubmitting(false);
    }
  };

  // Render waiting screen if it's not this player's turn
  if (!isMyTurn) {
    return (
      <div className="night-waiting">
        <div className="night-waiting-content">
          {hasCompletedTurn ? (
            <>
              <h2>Aksiyonunu tamamladın!</h2>
              <p>Diğer oyuncuların tamamlaması bekleniyor...</p>
            </>
          ) : (
            <>
              <h2>Sıra başkasında</h2>
              <div className="night-waiting-animation"></div>
              <p className="night-waiting-note">Sıran geldiğinde ekran otomatik olarak güncellenecektir.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // If showing an action result, prevent any other UI changes
  // This ensures the result stays on screen until explicit button press
  if (isShowingActionResult || actionResult) {
    return (
      <div className="night-action-result">
        <div className="night-action-result-content">
          {actionResult.type === 'player_card' && (
            <>
              <h2>Kart Gösterildi</h2>
              <p>
                <strong>{actionResult.playerName}</strong> oyuncusunun kartı:
              </p>
              <Card cardId={actionResult.cardId} size="large" />
              {cardData.action === 'COPY_PLAYER_CARD' && (
                <p className="night-action-note">
                  Bu karta dönüştün!
                </p>
              )}
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant="primary">
                  Devam
                </Button>
              </div>
            </>
          )}

          {actionResult.type === 'center_card' && (
            <>
              <h2>Merkez Kart Gösterildi</h2>
              <p>
                Ortadaki <strong>{actionResult.position}.</strong> kart:
              </p>
              <Card cardId={actionResult.cardId} size="large" />
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant="primary">
                  Devam
                </Button>
              </div>
            </>
          )}

          {actionResult.type === 'two_player_cards' && (
            <>
              <h2>İki Kart Görüntülendi</h2>
              <p>
                <strong>{actionResult.firstPlayerName}</strong> oyuncusunun kartı:
              </p>
              <Card cardId={actionResult.firstCardId} size="medium" />
              <p>
                <strong>{actionResult.secondPlayerName}</strong> oyuncusunun kartı:
              </p>
              <Card cardId={actionResult.secondCardId} size="medium" />
              <p className="night-action-note">
                {actionResult.changeMessage}
              </p>
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant="primary">
                  Devam
                </Button>
              </div>
            </>
          )}

          {actionResult.type === 'random_center_card' && (
            <>
              <h2>Rastgele Merkez Kartı</h2>
              <p>
                Ortadaki <strong>{actionResult.position}.</strong> kart ile yer değiştirdin:
              </p>
              <Card cardId={actionResult.cardId} size="large" />
              <p className="night-action-note">
                {actionResult.message}
              </p>
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant="primary">
                  Devam
                </Button>
              </div>
            </>
          )}

          {actionResult.type === 'steal_card' && (
            <>
              <h2>Kart Çalındı</h2>
              <p>
                <strong>{actionResult.playerName}</strong> oyuncusunun kartını çaldın:
              </p>
              <Card cardId={actionResult.cardId} size="large" />
              <p className="night-action-note">
                {actionResult.message}
              </p>
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant="primary">
                  Devam
                </Button>
              </div>
            </>
          )}

          {(actionResult.type === 'success' || actionResult.type === 'error') && (
            <>
              <h2>{actionResult.type === 'success' ? 'Başarılı!' : 'Hata!'}</h2>
              <p>{actionResult.message}</p>
              <div className="night-action-confirm">
                <Button onClick={handleCloseActionResult} variant={actionResult.type === 'error' ? "secondary" : "primary"}>
                  {actionResult.type === 'error' ? 'Tekrar Dene' : 'Devam'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Render appropriate action UI based on card type
  return (
    <div className="night-action">
      <div className="night-action-header">
        <h2>Senin Sıran!</h2>
        <p className="night-action-instruction">{cardData?.actionDescription || 'Aksiyonunu gerçekleştir'}</p>
      </div>
      
      <div className="night-action-content">
        {/* Render player selection for actions that target players */}
        {['VIEW_PLAYER_CARD', 'COPY_PLAYER_CARD', 'SILENCE_PLAYER', 'REVEAL_PLAYER_CARD', 'STEAL_CARD', 'REVEAL_AT_END', 'VIEW_TWO_CARDS_CHANGE_ROLE', 'SWAP_PLAYER_CARDS'].includes(cardData?.action) && (
          <div className="night-action-player-selection">
            <h3>{(cardData?.action === 'VIEW_TWO_CARDS_CHANGE_ROLE' || cardData?.action === 'SWAP_PLAYER_CARDS') ? 'İlk oyuncu seç:' : 'Bir oyuncu seç:'}</h3>
            <div className="night-action-players">
              {lobby.players.filter(p => p.id !== playerId).map(player => (
                <div 
                  key={player.id}
                  className={`night-action-player ${selectedPlayerId === player.id ? 'night-action-player--selected' : ''}`}
                  onClick={() => setSelectedPlayerId(player.id)}
                >
                  <div className="night-action-player-avatar">{player.name.charAt(0)}</div>
                  <div className="night-action-player-name">{player.name}</div>
                  {selectedPlayerId === player.id && (
                    <div className="night-action-player-check">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Second player selection for actions that need two players */}
        {(cardData?.action === 'VIEW_TWO_CARDS_CHANGE_ROLE' || cardData?.action === 'SWAP_PLAYER_CARDS') && selectedPlayerId && (
          <div className="night-action-player-selection">
            <h3>İkinci oyuncu seç:</h3>
            <div className="night-action-players">
              {lobby.players.filter(p => p.id !== playerId && p.id !== selectedPlayerId).map(player => (
                <div 
                  key={player.id}
                  className={`night-action-player ${secondSelectedPlayerId === player.id ? 'night-action-player--selected' : ''}`}
                  onClick={() => setSecondSelectedPlayerId(player.id)}
                >
                  <div className="night-action-player-avatar">{player.name.charAt(0)}</div>
                  <div className="night-action-player-name">{player.name}</div>
                  {secondSelectedPlayerId === player.id && (
                    <div className="night-action-player-check">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Render center card selection */}
        {['VIEW_CENTER_CARD', 'SWAP_WITH_CENTER'].includes(cardData?.action) && (
          <div className="night-action-center-selection">
            <h3>Ortadaki bir kart seç:</h3>
            <div className="night-action-center-cards">
              {[0, 1, 2].map(index => (
                <div
                  key={index}
                  className={`night-action-center-card ${selectedCenterCardIndex === index ? 'night-action-center-card--selected' : ''}`}
                  onClick={() => setSelectedCenterCardIndex(index)}
                >
                  <div className="night-action-center-card-back">
                    <span>{index + 1}</span>
                  </div>
                  {selectedCenterCardIndex === index && (
                    <div className="night-action-center-card-check">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* For cards with no action, just show a message */}
        {cardData?.action === 'NONE' && (
          <div className="night-action-none">
            <p>Bu kart için herhangi bir aksiyon yok. Tamamla butonuna basarak devam et.</p>
          </div>
        )}
      </div>
      
      <div className="night-action-footer">
        <Button 
          onClick={handleSubmitAction} 
          disabled={
            isSubmitting || 
            (cardData?.action === 'VIEW_PLAYER_CARD' && !selectedPlayerId) ||
            (cardData?.action === 'COPY_PLAYER_CARD' && !selectedPlayerId) ||
            (cardData?.action === 'SILENCE_PLAYER' && !selectedPlayerId) ||
            (cardData?.action === 'REVEAL_PLAYER_CARD' && !selectedPlayerId) ||
            (cardData?.action === 'STEAL_CARD' && !selectedPlayerId) ||
            (cardData?.action === 'REVEAL_AT_END' && !selectedPlayerId) ||
            (cardData?.action === 'VIEW_CENTER_CARD' && selectedCenterCardIndex === null) ||
            (cardData?.action === 'SWAP_WITH_CENTER' && selectedCenterCardIndex === null) ||
            (cardData?.action === 'SWAP_PLAYER_CARDS' && (!selectedPlayerId || !secondSelectedPlayerId)) ||
            (cardData?.action === 'VIEW_TWO_CARDS_CHANGE_ROLE' && (!selectedPlayerId || !secondSelectedPlayerId))
          }
          fullWidth={true}
        >
          {isSubmitting ? 'İşleniyor...' : 'Tamamla'}
        </Button>
      </div>
    </div>
  );
};

export default NightPhase;
